import { Container, NineSliceSprite, Sprite, Texture, type Text } from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v8";
import type { GameVisualAssets } from "../../assets/GameVisualAssets";
import { FEATURE_FINISHED_TUNING } from "../../tuning/featureFinishedTuning";
import { Button, SpinePanelMotion, hideSlotAttachments, socketContainerToBone } from "@slot/presentation";
import type { PresentationTaskRunner, SpineDriver, ViewportStore } from "@slot/presentation";
import { ModalChrome, bindTap, createLocalizedModalLabel, createModalLabel } from "@slot/game-shell";
import type { FeatureFinishedPort, LocalizationService } from "@slot/game-shell";

const CONTAINER_SOCKET_BONE = "total_board";
const GOLD = 0xffd600;
const ORANGE = 0xff9900;
const INK = 0x2c2a2a;
const BUTTON_INK = 0x252525;
const CONTENT_LIMITS = { desktop: { maxWidth: 1534 }, mobile: { maxWidth: 1534 }, mobile_landscape: {} } as const;
const NUMBER_GUIDE_SLOT = "number_placeholder";

/** `ModalLayer/GameModalContainer/FeatureFinishedView` geometry from the scene dump (authored y-up, stored y-down). */
// Both layouts put their resting amount at the grid's centre (see ModalChrome's `content`), measured 2026-10-04.
// Landscape: not yet.
const SIMPLE_GEOMETRY = {
  content: { width: 1534, height: 1064, y: { desktop: 65, mobile: -200, mobile_landscape: 65 }, shiftY: { desktop: -205.9, mobile: -55.4, mobile_landscape: 0 } },
  animatedContainer: { y: -46.3 },
  amount: { y: 117.5, fontSize: 145.6 },
  subtitle: { y: 244.2, fontSize: 78.3 },
} as const;
const OFFER_GEOMETRY = {
  // On desktop the amount stood above the grid's centre, so this layout moves down there.
  content: { width: 1534, height: 1526, y: { desktop: 2.4, mobile: -200, mobile_landscape: 2.4 }, shiftY: { desktop: 131.1, mobile: 219.0, mobile_landscape: 0 } },
  panelBg: { y: -295.8, width: 1534, height: 934 },
  title: { y: -308.6, fontSize: 103.4 },
  amount: { y: -156.9, fontSize: 145.6 },
  subtitle: { y: -30.2, fontSize: 78.3 },
  offerContainer: { y: 432.8, width: 930, height: 523, tint: 0xffecaf, border: 32 },
  offerTitle: { y: -178.1, fontSize: 55 },
  cost: { y: -38.2, fontSize: 55 },
  button: { y: 133.4, x: 174.7, width: 334, height: 116, border: 16, fontSize: 57.6 },
  declineTint: 0xe69393,
  buyTint: 0x93e01c,
} as const;

/**
 * Two layouts behind one `FeatureFinishedPort`: the SIMPLE dismiss-only summary (`total_win`
 * Spine, `simpleRoot`) and the extra-spins OFFER (`offerRoot`: plain panel background + Buy / Decline).
 * SIMPLE mode: the Spine plays `in` → `idle` (loop) on enter and `out` on exit, the dim + hint fade on
 * the chrome clock (the shell's `ModalChrome` and presentation's `SpinePanelMotion`, decision 46); the AnimatedContainer (summary + subtitle) is socketed to the Spine's `total_board`
 * bone. OFFER mode has no Spine and no fade root in this scene — enter/exit are immediate.
 */
export class FeatureFinishedView extends Container implements FeatureFinishedPort {
  private readonly skipButton: Container;
  private readonly buyButton: Button;
  private readonly declineButton: Button;
  private readonly offerRoot: ModalChrome;
  public readonly hasOffer = true;
  private readonly simpleRoot: ModalChrome;
  /** SIMPLE mode: the total_win Spine's in / idle / out with the dim and the hint fading beside it. */
  private readonly motion: SpinePanelMotion;
  private readonly summaryLabel: Text;
  private readonly offerSummaryLabel: Text;
  private readonly costLabel: Text;

  public constructor(input: {
    assets: GameVisualAssets;
    viewport: ViewportStore;
    localization: LocalizationService;
    spineDriver: SpineDriver;
    runner: PresentationTaskRunner;
  }) {
    super({ label: "FeatureFinishedPanel" });
    const { localization } = input;
    const chromeInput = { viewport: input.viewport, localization, runner: input.runner };

    // ── simpleRoot ──
    const sg = SIMPLE_GEOMETRY;
    this.simpleRoot = new ModalChrome({ label: "simpleRoot", ...chromeInput, content: { ...sg.content, limits: CONTENT_LIMITS } });
    this.addChild(this.simpleRoot);
    this.skipButton = this.simpleRoot.skipButton;

    let skeleton: Spine | null = null;
    const data = input.assets.skeletons.get("animations/spine/total_win.skeleton");
    if (data) {
      skeleton = new Spine({ skeletonData: data, autoUpdate: false });
      skeleton.label = "spine";
      input.spineDriver.attach(skeleton);
      this.simpleRoot.content.addChild(skeleton);
      hideSlotAttachments(skeleton, NUMBER_GUIDE_SLOT);
    }
    this.motion = new SpinePanelMotion({ surface: this.simpleRoot, skeleton, runner: input.runner, tuning: FEATURE_FINISHED_TUNING.anim });
    const animatedContainer = new Container({ label: "AnimatedContainer" });
    animatedContainer.position.set(0, sg.animatedContainer.y);
    this.summaryLabel = createModalLabel({ ...sg.amount, color: ORANGE, label: "AmountLabel" });
    animatedContainer.addChild(
      this.summaryLabel,
      createLocalizedModalLabel({ localization, key: "featurefinished.subtitle", fallback: "in Free Spins", ...sg.subtitle, color: ORANGE, label: "SubTitle" }),
    );
    this.simpleRoot.content.addChild(animatedContainer);
    if (skeleton) socketContainerToBone(skeleton, CONTAINER_SOCKET_BONE, animatedContainer, 1);

    // ── offerRoot ──
    const og = OFFER_GEOMETRY;
    this.offerRoot = new ModalChrome({ label: "offerRoot", ...chromeInput, content: { ...og.content, limits: CONTENT_LIMITS }, continueText: false });
    this.addChild(this.offerRoot);
    const offerContent = this.offerRoot.content;
    const panelBg = new Sprite({ texture: input.assets.textures.featurePanelBg ?? Texture.EMPTY, label: "panelBg" });
    panelBg.anchor.set(0.5);
    panelBg.setSize(og.panelBg.width, og.panelBg.height);
    panelBg.position.set(0, og.panelBg.y);
    this.offerSummaryLabel = createModalLabel({ ...og.amount, color: GOLD, label: "AmountLabel" });
    offerContent.addChild(
      panelBg,
      createLocalizedModalLabel({ localization, key: "featuretrigger.title", fallback: "You Have Won", ...og.title, color: GOLD, label: "Title" }),
      this.offerSummaryLabel,
      createLocalizedModalLabel({ localization, key: "featuretrigger.subtitle", fallback: "Free Spins", ...og.subtitle, color: GOLD, label: "SubTitle" }),
    );

    const offerContainer = new Container({ label: "offerContainer" });
    offerContainer.position.set(0, og.offerContainer.y);
    const box = nineSlice(input.assets.textures.border32, og.offerContainer.border, og.offerContainer.width, og.offerContainer.height, og.offerContainer.tint, "bg");
    this.costLabel = createModalLabel({ ...og.cost, color: INK, weight: "700", label: "CostLabel" });
    this.declineButton = offerButton({ localization, key: "common.decline", fallback: "No, thanks", tint: og.declineTint, x: -og.button.x, texture: input.assets.textures.border16, label: "DeclineButton" });
    this.buyButton = offerButton({ localization, key: "common.buy", fallback: "Buy", tint: og.buyTint, x: og.button.x, texture: input.assets.textures.border16, label: "BuyButton" });
    offerContainer.addChild(
      box,
      createLocalizedModalLabel({ localization, key: "featurefinished.offer.title", fallback: "Buy extra free spins?", ...og.offerTitle, color: INK, weight: "700", label: "Label" }),
      this.costLabel,
      this.declineButton,
      this.buyButton,
    );
    offerContent.addChild(offerContainer);
  }

  public showSimple(): void {
    this.motion.preSeat();
    this.simpleRoot.visible = true;
    this.offerRoot.visible = false;
  }

  public showOffer(): void {
    this.simpleRoot.visible = false;
    this.offerRoot.visible = true;
  }

  public hide(): void {
    this.simpleRoot.visible = false;
    this.offerRoot.visible = false;
    this.motion.stop();
  }

  public setSummary(text: string): void {
    this.summaryLabel.text = text ?? "";
    this.offerSummaryLabel.text = text ?? "";
  }

  public setCostText(text: string): void {
    this.costLabel.text = text ?? "";
  }

  public setSkipEnabled(enabled: boolean): void {
    this.skipButton.visible = enabled;
  }

  public setBuyEnabled(enabled: boolean): void {
    this.buyButton.visible = enabled;
  }

  public setDeclineEnabled(enabled: boolean): void {
    this.declineButton.visible = enabled;
  }

  public bindSkipTap(onPress: () => void): () => void {
    return bindTap(this.skipButton, onPress);
  }

  public bindBuyTap(onPress: () => void): () => void {
    return bindTap(this.buyButton, onPress);
  }

  public bindDeclineTap(onPress: () => void): () => void {
    return bindTap(this.declineButton, onPress);
  }

  /** SIMPLE mode: `in` then the idle loop, the dim fading in alongside. OFFER mode: immediate. */
  public async playEnterAnimation(): Promise<void> {
    if (this.offerRoot.visible) return;
    await this.motion.enter();
  }

  /** SIMPLE mode: `out` with the dim and the hint fading out. OFFER mode: immediate. */
  public async playExitAnimation(): Promise<void> {
    if (this.offerRoot.visible) return;
    await this.motion.exit();
  }

  public async fadeInContinueText(): Promise<void> {
    await this.motion.fadeInContinueText();
  }

  public dispose(): void {
    this.simpleRoot.dispose();
    this.offerRoot.dispose();
    this.destroy({ children: true });
  }
}

/** A tinted nine-slice box; a plain tinted rect when the border texture is missing. */
function nineSlice(texture: Texture | null, border: number, width: number, height: number, tint: number, label: string): NineSliceSprite {
  const t = texture ?? Texture.WHITE;
  const b = texture ? border : 0;
  const sprite = new NineSliceSprite({ texture: t, leftWidth: b, topHeight: b, rightWidth: b, bottomHeight: b, width, height, tint, label });
  sprite.position.set(-width / 2, -height / 2);
  return sprite;
}

function offerButton(input: { localization: LocalizationService; key: string; fallback: string; tint: number; x: number; texture: Texture | null; label: string }): Button {
  const g = OFFER_GEOMETRY.button;
  const view = new Container({ label: "view" });
  view.addChild(
    nineSlice(input.texture, g.border, g.width, g.height, input.tint, "sprite"),
    createLocalizedModalLabel({ localization: input.localization, key: input.key, fallback: input.fallback, y: 0, fontSize: g.fontSize, color: BUTTON_INK, weight: "700", label: "Label" }),
  );
  const button = new Button({ view, label: input.label });
  button.position.set(input.x, g.y);
  return button;
}
